import {WriteButton} from '../../common/buttons/WriteActions';
import {mobileDialogSx} from '../../common/dialogs/mobileDialogSx';
import {LocalizedDateField} from '../../common/dates/LocalizedDateField';
import {getCurrencySymbol} from '../../../common/currency';
import {useEffect, useState} from 'react';
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormLabel, InputAdornment, Radio, RadioGroup, Stack, TextField, Typography, useMediaQuery, useTheme} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import {ItemDto} from '../../../models/Items/IItem';
import {CreateStockRequest} from '../../../models/Items/IStock';
import {UNIT_OF_WEIGHT} from '@mybake/ui/common/dictionary';
import {stockAPI} from '../../../services/StockServices';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {useCurrency} from '../../../hooks/useCurrency';
import {getApiErrorMessage, getApiFieldErrors} from '../../../common/apiErrors';
import {LocalizedDecimalField} from '../../common/inputs/LocalizedDecimalField';

type Props = {
    item: ItemDto;
    open: boolean;
    onClose: () => void;
    onSaved?: () => void;
};

const localToday = () => {
    const date = new Date();
    const offset = date.getTimezoneOffset();
    return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 10);
};

export const AddPurchaseDialog = (props: Props) => props.open ? <PurchaseDialogContent key={props.item.id} {...props}/> : null;

const PurchaseDialogContent = ({item, open, onClose, onSaved}: Props) => {
    const {currency} = useCurrency();
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    const {t, i18n} = useAppTranslation();
    const [addStock, {isLoading, error}] = stockAPI.useAddStockMutation();
    const [purchaseMode, setPurchaseMode] = useState<'pieces' | 'packages'>('pieces');
    const [stock, setStock] = useState<CreateStockRequest>({
        productionCompany: '',
        itemId: item.id,
        count: 0,
        unitValue: item.unitOfWeight === UNIT_OF_WEIGHT.PCS ? 1 : null,
        price: 0,
        weightOfPiece: item.unitOfWeight === UNIT_OF_WEIGHT.G ? 0 : 1,
        purchaseDate: localToday(),
    });

    useEffect(() => {
        if (!open) return;

        setPurchaseMode('pieces');
        setStock({
            productionCompany: '',
            itemId: item.id,
            count: 0,
            unitValue: item.unitOfWeight === UNIT_OF_WEIGHT.PCS ? 1 : null,
            price: 0,
            weightOfPiece: item.unitOfWeight === UNIT_OF_WEIGHT.G ? 0 : 1,
            purchaseDate: localToday(),
        });
    }, [item.id, item.unitOfWeight, open]);

    const setNumber = (field: 'count' | 'price' | 'weightOfPiece') => (event: React.ChangeEvent<HTMLInputElement>) => {
        setStock(previous => ({...previous, [field]: Number(event.target.value)}));
    };

    const isPieces = item.unitOfWeight === UNIT_OF_WEIGHT.PCS;
    const isWeight = item.unitOfWeight === UNIT_OF_WEIGHT.G;
    const formatQuantity = (value: number) => value.toLocaleString(i18n.resolvedLanguage || i18n.language, {maximumFractionDigits: 10});
    const isPackagePurchase = isPieces && purchaseMode === 'packages';
    const hasValidPieceCounts = !isPieces || (Number.isInteger(stock.count) &&
        (!isPackagePurchase || (Number.isInteger(stock.unitValue) && (stock.unitValue ?? 0) > 0)));
    const totalPieces = isPieces ? stock.count * (stock.unitValue ?? 1) : 0;
    const isValid = stock.count > 0 && stock.price > 0 && hasValidPieceCounts &&
        (item.unitOfWeight !== UNIT_OF_WEIGHT.G || stock.weightOfPiece > 0) && !!stock.purchaseDate;

    const handleSubmit = async () => {
        if (!isValid) return;

        await addStock({stock, weightUnit: item.unitOfWeight as UNIT_OF_WEIGHT}).unwrap();
        onSaved?.();
        onClose();
    };

    return (
        <Dialog sx={mobileDialogSx} open={open} onClose={isLoading ? undefined : onClose} fullWidth maxWidth="xs"
                PaperProps={{sx: {borderRadius: 3}}}>
            <DialogTitle>{t('items:add_purchase')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <Typography color="text.secondary" variant="body2">
                        {item.name} · {item.unitOfWeight}
                    </Typography>
                    {error && <Alert severity="error">{getApiErrorMessage(error, t)}</Alert>}
                    <LocalizedDecimalField
                        autoFocus
                        required
                        fullWidth
                        label={t(isWeight ? 'items:weight_package_quantity' : isPackagePurchase ? 'items:package_quantity' : 'items:purchase_quantity')}
                        inputProps={{min: 0, step: 'any'}}
                        value={stock.count || undefined}
                        onValueChange={count => setStock(previous => ({...previous, count: count ?? 0}))}
                        error={!!getApiFieldErrors(error, t).count}
                        helperText={getApiFieldErrors(error, t).count}
                    />
                    {isPieces && (
                        <FormControl>
                            <FormLabel>{t('items:purchase_mode')}</FormLabel>
                            <RadioGroup row value={purchaseMode} onChange={(event) => {
                                const mode = event.target.value as 'pieces' | 'packages';
                                setPurchaseMode(mode);
                                setStock(previous => ({...previous, unitValue: mode === 'pieces' ? 1 : null}));
                            }}>
                                <FormControlLabel value="pieces" control={<Radio/>} label={t('items:individually')}/>
                                <FormControlLabel value="packages" control={<Radio/>} label={t('items:in_packages')}/>
                            </RadioGroup>
                        </FormControl>
                    )}
                    {isPackagePurchase && (
                        <TextField
                            required
                            fullWidth
                            label={t('items:pieces_per_package')}
                            helperText={t('items:pieces_per_package_hint')}
                            type="number"
                            inputProps={{min: 1, step: 1}}
                            value={stock.unitValue ?? ''}
                            onChange={(event) => setStock(previous => ({
                                ...previous,
                                unitValue: event.target.value === '' ? null : Number(event.target.value),
                            }))}
                        />
                    )}
                    {isPieces && stock.count > 0 && (!isPackagePurchase || (stock.unitValue ?? 0) > 0) && (
                        <Typography variant="body2" color="text.secondary">
                            {t('items:total_pieces')}: <Box component="span" fontWeight={700}>{totalPieces}</Box>
                        </Typography>
                    )}
                    <LocalizedDecimalField
                        required
                        fullWidth
                        label={t('items:total_purchase_price')}
                        inputProps={{min: 0, step: '0.01'}}
                        InputProps={{endAdornment: <InputAdornment position="end">{getCurrencySymbol(currency)}</InputAdornment>}}
                        value={stock.price || undefined}
                        onValueChange={price => setStock(previous => ({...previous, price: price ?? 0}))}
                    />
                    {item.unitOfWeight === UNIT_OF_WEIGHT.G && (
                        <LocalizedDecimalField
                            required
                            fullWidth
                            label={t('items:package_weight_grams')}
                            inputProps={{min: 0, step: 'any'}}
                            value={stock.weightOfPiece || undefined}
                            onValueChange={weightOfPiece => setStock(previous => ({...previous, weightOfPiece: weightOfPiece ?? 0}))}
                            error={!!getApiFieldErrors(error, t).weightOfPiece}
                            helperText={getApiFieldErrors(error, t).weightOfPiece || t('items:weight_of_piece_help')}
                        />
                    )}
                    {isWeight && stock.count > 0 && stock.weightOfPiece > 0 && Number.isFinite(stock.count * stock.weightOfPiece) && (
                        <Typography variant="body2" color="text.secondary" aria-live="polite">
                            {t('items:purchase_weight_summary', {
                                packages: formatQuantity(stock.count),
                                weight: formatQuantity(stock.weightOfPiece),
                                total: formatQuantity(stock.count * stock.weightOfPiece),
                            })}
                        </Typography>
                    )}
                    <LocalizedDateField
                        required
                        fullWidth
                        label={t('items:purchase_date')}
                        type="date"
                        value={stock.purchaseDate ?? ''}
                        onChange={(event) => setStock(previous => ({...previous, purchaseDate: event.target.value}))}
                        InputLabelProps={{shrink: true}}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2, gap: 1}}>
                <Button onClick={onClose} disabled={isLoading}>{t('common:cancel')}</Button>
                <Box sx={{display: {xs: 'none', sm: 'block'}, flex: 1}}/>
                <WriteButton variant="contained" onClick={handleSubmit} disabled={!isValid || isLoading}
                        sx={{'& .MuiButton-startIcon': {display: {xs: 'none', sm: 'inherit'}}}} startIcon={<AddShoppingCartIcon/>}>
                    {t(isMobile ? 'common:save' : 'items:save_purchase')}
                </WriteButton>
            </DialogActions>
        </Dialog>
    );
};
